/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useRef } from "react";
import { CapacityAllocationChart } from "kontrolEdge/components/Charts/CapacityAllocationChart";
import { DataCenterCapacityChart } from "kontrolEdge/components/Charts/DatacenterCapacityChart";
import { PortCapacityChart } from "kontrolEdge/components/Charts/PortCapacityChart";
import { ServiceKind, ServiceStatusCard } from "./OverviewComponents/ServiceStatusCard";
import { ChartCard } from "./OverviewComponents/ChartCards";

import { useAppSelector } from "@redux/hooks";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { PortUsageList } from "./OverviewComponents/PortUsageList";
import {
  FailoverItem,
  FailoverTableRow,
  Port,
} from "./OverviewComponents/Types";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { LoadingWrapper } from "kontrolEdge/components/Shared/LoadingWrapper";
import EmptyItems from "@shared/components/EmptyItems";
import GenericButton from "@ui-components/Button/GenericButton";
import { useManagerOverviewData } from "./OverviewComponents/hooks/useManagerOverviewData";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  normalizeToUnit,
  useManagerOverviewComputedData,
} from "./OverviewComponents/hooks/useManagerOverviewComputedData";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { useNavigate } from "react-router-dom";
import { chunk, useHScroll } from "./OverviewComponents/hooks/useHScroll";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

function ManagerOverview() {
  const api = useAxiosAuth0();
  const { tenantId, tenantName, partnerId } = useAppSelector(
    (state) => state.selection
  );
  const isViewingAllCustomers = tenantId === partnerId;

  const selection = useAppSelector((s) => s.selection);
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  let currentView: "GLOBAL_ADMIN" | "PARTNER" | "CUSTOMER" = tenantType as any;
  if (tenantType === "GLOBAL_ADMIN") {
    if (
      selection.partnerId &&
      selection.tenantId &&
      selection.partnerId !== selection.tenantId
    ) {
      currentView = "CUSTOMER";
    } else if (selection.partnerId) {
      currentView = "PARTNER";
    }
  }

  const TITLE_TO_KIND: Record<string, ServiceKind> = {
    "Enterprise Internet": "internet",
    "Ports": "port",
    "Cloud Connectivity": "cloud",
    "Data Center Interconnect": "datacenter",
    "FWaaS": "fwaas",
  };

  // General flag to show "Empty Items" if no service status is available.
  const { data, loading, hasData, isServiceFetched } = useManagerOverviewData(
    api,
    tenantId
  );
  const {
    filteredFailoverRecords,
    filteredMaintenanceRecords,
    filteredUsagePorts,
    serviceCards,
    aggregatedAllocated,
    capacityAllocationData,
    totalCapacity,
    totalAllocated,
    portCapacityPieData,
    legendDataPortCapacity,
    chartData,
    showPortCapacity,
    showDataCenterCapacity,
    showCapacityAllocationChart,
    datacenterCapacityData,
    datacenterCapacityLegend,
    chartUnit,
    portChartUnit,
  } = useManagerOverviewComputedData({
    data,
    tenantId,
    tenantName,
    isViewingAllCustomers,
    currentView,
  });

  const navigate = useNavigate();
  const isFetchingService = loading.service;
  const overlaySpinner = useProcessingOverlay(isFetchingService);

  const serviceStatusData = data.serviceStatus;
  const usageData = data.usage;
  const failover = data.failover;
  const maintenance = data.maintenance;

  const isFetchingUsage = loading.usage;
  const isFetchingFailover = loading.failover;
  const isFetchingMaintenance = loading.maintenance;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 320,
        behavior: "smooth",
      });
    }
  };
  const rows = useMemo(() => chunk(serviceCards, 3), [serviceCards]);
  const statusScroll = useHScroll(320, [serviceCards.length]);

  const handlePortCardClick = useCallback(
    (port: Port) => {
      if (!port?.id) return;
      navigate(`/kontrol/port-details?portId=${port.id}`, {
        state: {
          selectedPortRow: port,
          portIdString: port.subscriptionId,
          from: "usage-list",
        },
      });
    },
    [navigate]
  );
  //------Loading the failover data inside the table in Outages
  const tableData: FailoverTableRow[] = useMemo(
    () =>
      filteredFailoverRecords
        .slice(0, 5)
        .map(({ date, description, duration, customer }: FailoverItem) => ({
          date,
          description,
          customer: customer?.name ?? "–",
          duration: formatDuration(duration),
        })) ?? [],
    [filteredFailoverRecords]
  );

  //------Loading the maintenance data inside the table in Outages
  const maintenanceData: FailoverTableRow[] = useMemo(
    () =>
      filteredMaintenanceRecords
        .slice(0, 5)
        .map(({ date, description, duration, customer }: FailoverItem) => ({
          date,
          description,
          customer: customer?.name ?? "–",
          duration: formatDuration(duration),
        })) ?? [],
    [filteredMaintenanceRecords]
  );

  //------Loading columns in the table
  const columns: GenericTableColumn<FailoverTableRow>[] = useMemo(() => {
    const cols: GenericTableColumn<FailoverTableRow>[] = [
      {
        header: "Date",
        accessor: "date",
        renderCell: (row) =>
          isFetchingFailover ? (
            <div className="h-4 w-24 bg-gray-300 animate-pulse rounded" />
          ) : (
            row.date
          ),
      },
      {
        header: "Description",
        accessor: "description",
        renderCell: (row) =>
          isFetchingFailover ? (
            <div className="h-4 w-48 bg-gray-300 animate-pulse rounded" />
          ) : (
            <div className="max-w-[280px] whitespace-nowrap overflow-x-auto">
              {row.description}
            </div>
          ),
      },
    ];

    if (isViewingAllCustomers) {
      cols.push({
        header: "Customer",
        accessor: "customer",
        renderCell: (row) =>
          isFetchingFailover ? (
            <div className="h-4 w-24 bg-gray-300 animate-pulse rounded" />
          ) : (
            <p className="text-sm text-semantic-text-secondary">
              {row.customer}
            </p>
          ),
      });
    }

    cols.push({
      header: "Duration",
      accessor: "duration",
      renderCell: (row) =>
        isFetchingFailover ? (
          <div className="h-4 w-16 bg-gray-300 animate-pulse rounded" />
        ) : (
          row.duration
        ),
    });

    return cols;
  }, [isViewingAllCustomers, isFetchingFailover]);

  //Skeleton
  const tableSkeleton = (
    <SkeletonTableRows rows={3} cols={1} rowHeight={16} />
  );

  return (
    <>
      {isServiceFetched && !hasData ? (
        <EmptyItems />
        ) : (


        <>
          {/* Service Status */}
          <div className="mt-8 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
            <h2 className="text-md px-6 py-2 mb-8 font-bold text-semantic-text-primary text-left border-b">
              Service Status
            </h2>
              <div className="py-4 px-4">
                {/* <div className="space-y-6"> */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {rows.flat().map(({ title, path, data, showWarning }) => {
                  const service: ServiceKind = TITLE_TO_KIND[title] ?? "internet";
                  return (
                    <div key={path} className="w-full">
                      <ServiceStatusCard
                        title={title}
                        data={data}
                        isLoading={isFetchingService}
                        path={path}
                        showWarning={showWarning}
                        service={service}
                      />
                    </div>
                  );
                  })}
                </div>
              </div>
          </div>

          {/* Charts Section */}
          <div className="mt-8 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
            <h2 className="text-md px-6 py-2 mb-8 font-bold text-semantic-text-primary text-left border-b">
              Performance Metrics
            </h2>
            <div className="grid grid-cols-2 gap-4 mb-5 px-4">
              {showPortCapacity && (
                <ChartCard
                  title={
                    isViewingAllCustomers
                      ? "Total allocated by customer"
                      : "Total port capacity"
                  }
                  subtitle={isViewingAllCustomers ? "" : "Allocated / Total"}
                  chart={
                    <PortCapacityChart
                      data={chartData}
                      isBreakdown={isViewingAllCustomers}
                      totalCapacityGbps={normalizeToUnit(
                        totalCapacity,
                        portChartUnit
                      )}
                      isViewingAll={isViewingAllCustomers}
                      centerValueMbps={totalAllocated}
                    />
                  }
                  legendData={legendDataPortCapacity}
                  unit={portChartUnit}
                  hideValue={true}
                  isLoading={isFetchingService}
                />
              )}

              {showDataCenterCapacity && (
                <ChartCard
                  title="Data center port capacity"
                  subtitle="Allocated vs Available"
                  chart={
                    <DataCenterCapacityChart
                      data={datacenterCapacityData}
                      unit={chartUnit}
                    />
                  }
                  legendData={datacenterCapacityData}
                  extraLegendData={datacenterCapacityLegend}
                  unit={chartUnit}
                  isLoading={isFetchingService}
                />
              )}

              {showCapacityAllocationChart && (
                <ChartCard
                  title="Capacity allocation distribution"
                  subtitle=""
                  chart={
                    <CapacityAllocationChart data={capacityAllocationData} />
                  }
                  legendData={capacityAllocationData}
                  isLoading={isFetchingService}
                />
              )}
            </div>
            {(isFetchingUsage ||
              (usageData?.data && usageData.data.length > 0)) && (
              <div className="relative py-4">
                <div className="px-4">
                  {/* Scrollable container WITH REF */}
                  <div
                    ref={scrollContainerRef}
                    className="overflow-x-auto"
                    style={{ scrollbarGutter: "stable both-edges" }}
                  >
                    <div className="flex gap-5 pr-4">
                      {usageData?.data && (
                        <LoadingWrapper
                          isLoading={isFetchingUsage}
                          skeleton={tableSkeleton}
                        >
                          <PortUsageList
                            ports={filteredUsagePorts}
                            onCardClick={handlePortCardClick}
                          />
                        </LoadingWrapper>
                      )}
                    </div>
                  </div>
                  {/* Floating scroll button */}
                  {filteredUsagePorts.length > 2 && (
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 pr-2 z-20">
                      <GenericButton
                        variant="outlined"
                        size="lg"
                        iconRightName="arrowRight"
                        isIconOnly
                        onClick={handleScrollRight}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Maintenance and Outages */}
          <div className="grid grid-cols-2">
            <div className="mt-8 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
              <h2 className="text-md px-6 py-2 mb-8 font-bold text-semantic-text-primary text-left border-b">
                Outages
              </h2>
              <div className="mb-5 px-4">
                <LoadingWrapper
                  isLoading={isFetchingFailover}
                  skeleton={tableSkeleton}
                >
                  <GenericTable
                    columns={columns}
                    // data={tableData}
                    data={isFetchingFailover ? Array(5).fill({}) : tableData}
                    tableClassName="w-full"
                    isFooterVisible={false}
                  />
                </LoadingWrapper>
                <div className="flex justify-start py-5 gap-5">
                  {/* <GenericTag label={`Last Check: ${formattedLastCheck}`} /> */}
                </div>
                {/* <div className='flex justify-end mb-10 mr-5'>
					<GenericButton label="See all" size="sm" onClick={() => navigate('/kontrol/outages', {state: {selectedView: "outages"}})} disabled={true}/>
				</div> */}
              </div>
            </div>
            <div className="mt-8 rounded-md border border-gray-300 bg-semantic-bg-primary mx-3">
              <h2 className="text-md px-6 py-2 mb-8 font-bold text-semantic-text-primary text-left border-b">
                Maintenance
              </h2>
              <div className="mb-5 px-4">
                <LoadingWrapper
                  isLoading={isFetchingMaintenance}
                  skeleton={tableSkeleton}
                >
                  <GenericTable
                    columns={columns}
                    data={
                      isFetchingMaintenance
                        ? Array(5).fill({})
                        : maintenanceData
                    }
                    // data={maintenanceData}
                    tableClassName="w-full"
                    isFooterVisible={false}
                  />
                </LoadingWrapper>
                {/* <div className='flex justify-end mb-10 mr-5'>
					<GenericButton label="See all" size="sm" onClick={() => navigate('/kontrol/outages', {state: {selectedView: "maintenance"}})}  disabled={false}/>
				</div> */}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default ManagerOverview;
